/**
 * Error Classes Index
 *
 * Why: Clean exports for all error classes
 * What: Re-exports all error classes from single location
 *
 * Benefit: Cleaner imports throughout the application
 *
 * Usage:
 *      import {ApiError,NetworkError} from '@/core/error/classes';
 */

import { ApiError } from "./ApiError";
import { AuthenticationError } from "./AuthenticationError";
import { AppError } from "./base/AppError";
import { CancellationError } from "./CancellationError";
import { NetworkError } from "./NetworkError";

export { AppError, type AppErrorOptions } from "./base/AppError";
export { ApiError, type ApiErrorOptions } from "./ApiError";
export { NetworkError, type NetworkErrorOptions } from "./NetworkError";
export { TimeoutError, type TimeoutErrorOptions } from "./TimeoutError";
export {
  CancellationError,
  CancellationReason,
  type CancellationErrorOptions,
} from "./CancellationError";

// Auth errors
export {
  AuthenticationError,
  AuthAction,
  type AuthenticationErrorOptions,
} from "./AuthenticationError";
export {
  AuthorizationError,
  type AuthorizationErrorOptions,
} from "./AuthorizationError";

// Rate limiting
export { RateLimitError, type RateLimitErrorOptions } from "./RateLimitError";

/**
 * Type guard to check if error is an AppError
 */
export const isAppError = (error: unknown): error is AppError => {
  return error instanceof AppError;
};

/**
 * Type guard to check if error is an ApiError
 */
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};

/**
 * Type guard to check if error is a NetworkError
 */
export const isNetworkError = (error: unknown): error is NetworkError => {
  return error instanceof NetworkError;
};

/**
 * Type guard to check if error is an AuthenticationError
 */
export const isAuthenticationError = (
  error: unknown,
): error is AuthenticationError => {
  return error instanceof AuthenticationError;
};

/**
 * Type guard to check if error is a CancellationError
 */
export const isCancellationError = (
  error: unknown,
): error is CancellationError => {
  return error instanceof CancellationError;
};
